/**
 * subscriptionPlansCache — module-singleton promise cache for the
 * `/api/subscriptions/plans` endpoint.
 *
 * Both `SubscriptionContext` and `TenantSubscriptionContext` mount
 * concurrently in the admin shell and each fired their own request.
 * The plan list is global static-ish data (Starter/Professional/etc.)
 * so the same response serves both, and a long TTL is safe — plans
 * change at most a few times per year via admin tooling.
 */

const TTL_MS = 5 * 60_000; // 5 min — plans rarely change at runtime

interface PlansResponse {
  success?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  plans?: any[];
  [k: string]: unknown;
}

let cached: { promise: Promise<PlansResponse>; resolvedAt: number | null } | null = null;

export function getSubscriptionPlans(): Promise<PlansResponse> {
  const now = Date.now();
  if (cached) {
    if (cached.resolvedAt === null) return cached.promise;
    if (now - cached.resolvedAt < TTL_MS) return cached.promise;
  }
  const promise = fetch('/api/subscriptions/plans')
    .then((r) => r.json() as Promise<PlansResponse>)
    .finally(() => {
      if (cached && cached.promise === promise) cached.resolvedAt = Date.now();
    });
  cached = { promise, resolvedAt: null };
  promise.catch(() => {
    if (cached && cached.promise === promise) cached = null;
  });
  return promise;
}

export function invalidateSubscriptionPlans(): void {
  cached = null;
}
